import type { ToolGrant } from '@netsuite-ai/types';
import { useTools } from '../../../lib/skills.queries';

interface Props {
  value: ToolGrant[];
  onChange: (v: ToolGrant[]) => void;
}

export default function ToolGrants({ value, onChange }: Props) {
  const { data: tools = [], isLoading } = useTools();

  function isEnabled(identifier: string) {
    return value.some((g) => g.toolIdentifier === identifier);
  }

  function toggle(identifier: string) {
    if (isEnabled(identifier)) {
      onChange(value.filter((g) => g.toolIdentifier !== identifier));
    } else {
      onChange([
        ...value,
        { toolType: 'netsuite', toolIdentifier: identifier },
      ]);
    }
  }

  if (isLoading) {
    return <p className="text-xs text-neutral-500">Loading tools…</p>;
  }

  if (tools.length === 0) {
    return (
      <p className="text-xs text-neutral-500">
        No tools available for this tenant.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {tools.map((tool) => (
        <div
          key={tool.identifier}
          className="flex items-center gap-3 rounded border border-neutral-200 bg-neutral-50 px-3 py-2"
        >
          <span className="bg-danger shrink-0 rounded px-2 py-0.5 text-xs font-medium text-white">
            {tool.action}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-neutral-900">{tool.label}</p>
            <p className="text-xs text-neutral-500">{tool.description}</p>
          </div>
          <Toggle
            enabled={isEnabled(tool.identifier)}
            onChange={() => toggle(tool.identifier)}
          />
        </div>
      ))}
    </div>
  );
}

function Toggle({
  enabled,
  onChange,
}: {
  enabled: boolean;
  onChange: () => void;
}) {
  return (
    <button
      role="switch"
      aria-checked={enabled}
      onClick={onChange}
      className={[
        'relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200',
        enabled ? 'bg-blue-500' : 'bg-neutral-300',
      ].join(' ')}
    >
      <span
        className={[
          'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200',
          enabled ? 'translate-x-4' : 'translate-x-0',
        ].join(' ')}
      />
    </button>
  );
}
